import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FeedbackActivityRecorder,
  getBuildVersion,
  toFeedbackEnvironment,
} from '@jfw/angular/shared/common-components/feedback';
import {
  AccountService,
  isAccountContextSuite,
} from '@jfw/angular/shared/services';
import { FeedbackClientContext, FeedbackSourcePortalEnum } from '@jfw/types';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth/auth.service';

/**
 * EXT-6172: collects the context attached to a feedback submission -- the
 * page and module, recent navigation, recent failures, client details and the
 * loaded account. Navigation and failures come from the shared
 * FeedbackActivityRecorder (EXT-6239); this adds Platform Portal's module and
 * loaded account.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackContextService {
  private readonly recorder = inject(FeedbackActivityRecorder);
  private readonly authService = inject(AuthService);
  private readonly accountService = inject(AccountService);

  private readonly currentModule = toSignal(this.authService.currentModule$, {
    initialValue: null,
  });
  private readonly currentSuite = toSignal(this.authService.currentSuite$, {
    initialValue: null,
  });

  public snapshot(): FeedbackClientContext {
    const module = this.currentModule();
    const deepest = this.recorder.deepestRoute();
    const account = isAccountContextSuite(this.currentSuite()?.enum)
      ? this.accountService._platformAccount()
      : undefined;

    return {
      portal: FeedbackSourcePortalEnum.PlatformPortal,
      environment: toFeedbackEnvironment(environment.envVersion),
      app: { version: getBuildVersion() },
      module: module
        ? {
            name: module.name,
            enumeration: module.enumeration,
            suite: module.suiteEnum ?? null,
          }
        : null,
      page: {
        name:
          deepest?.data?.['seoParams']?.metaTitle ??
          module?.name ??
          document.title,
        url: window.location.href,
        routePattern: this.recorder.routePattern(),
      },
      previousRoutes: this.recorder.previousRoutes(),
      client: this.recorder.clientInfo(),
      recentErrors: this.recorder.recentErrors(),
      // DB2 CHAR columns come back space-padded.
      account: account?.accountNumber?.trim()
        ? {
            number: account.accountNumber.trim(),
            name: account.accountName?.trim() ?? '',
          }
        : null,
    };
  }
}
